import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  GitBranch, 
  Plus, 
  Calendar, 
  User, 
  Code, 
  Play,
  Edit,
  GitMerge,
  Users,
  GitFork,
  Shield,
  Star,
  BarChart3,
  GitPullRequest
} from 'lucide-react';
import { getBranch, getPrompt, createPromptVersion, activatePromptVersion } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const BranchDetail = ({ user }) => {
  const { promptId, branchId } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateVersion, setShowCreateVersion] = useState(false);
  const [newVersion, setNewVersion] = useState({
    content: '',
    changeMessage: ''
  });

  useEffect(() => {
    loadData();
  }, [promptId, branchId]);

  const loadData = async () => {
    try {
      
      const [promptData, branchData] = await Promise.all([
        getPrompt(promptId),
        getBranch(branchId, user.id)
      ]);
      
      setPrompt(promptData);
      setBranch(branchData);
      
      // Set the active version content as default for new version
      const activeVersion = branchData.versions?.find(v => v.is_active);
      if (activeVersion) {
        setNewVersion(prev => ({ ...prev, content: activeVersion.content }));
      }
    } catch (error) {
      console.error('Failed to load branch data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!newVersion.content.trim()) return;

    try {
      await createPromptVersion(promptId, {
        content: newVersion.content,
        changeMessage: newVersion.changeMessage,
        authorId: user.id,
        branchId: branchId
      });

      setShowCreateVersion(false);
      setNewVersion({ content: '', changeMessage: '' });
      loadData(); // Reload to show new version
    } catch (error) {
      console.error('Failed to create version:', error);
    }
  };

  const handleActivateVersion = async (versionId) => {
    try {
      await activatePromptVersion(promptId, versionId);
      loadData(); // Reload to show updated active status
    } catch (error) {
      console.error('Failed to activate version:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!prompt || !branch) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Branch not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  const activeVersion = branch.versions?.find(v => v.is_active);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${promptId}/logs`} className="hover:text-primary-600">
          Logs
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">{branch.name}</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <h1 className="text-3xl font-bold text-gray-900">{prompt.name}</h1>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                <GitBranch className="h-4 w-4 mr-1" />
                {branch.name}
              </span>
              {branch.is_default && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  <Star className="h-3 w-3 mr-1" />
                  Default
                </span>
              )}
              {branch.is_protected && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  <Shield className="h-3 w-3 mr-1" />
                  Protected
                </span>
              )}
            </div>
          </div>
          <p className="text-gray-600">{prompt.description}</p>
          {branch.description && (
            <p className="text-gray-500 text-sm mt-1">Branch: {branch.description}</p>
          )}
        </div>
        
        <button
          onClick={() => setShowCreateVersion(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Version
        </button>
      </div>

      {/* Collaboration Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <Link 
            to={`/prompts/${promptId}`}
            className="py-2 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 hover:text-gray-700 hover:border-gray-300"
          >
            <Edit className="h-4 w-4 inline mr-1" />
            Code
          </Link>
          <Link 
            to={`/prompts/${promptId}/logs`}
            className="py-2 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 hover:text-gray-700 hover:border-gray-300"
          >
            <BarChart3 className="h-4 w-4 inline mr-1" />
            Logs
          </Link>
          <Link 
            to={`/prompts/${promptId}/pull-requests`}
            className="py-2 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 hover:text-gray-700 hover:border-gray-300"
          >
            <GitPullRequest className="h-4 w-4 inline mr-1" />
            Pull Requests
          </Link>
          <Link 
            to={`/prompts/${promptId}/collaborators`}
            className="py-2 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 hover:text-gray-700 hover:border-gray-300"
          >
            <Users className="h-4 w-4 inline mr-1" />
            Collaborators
          </Link>
          <Link 
            to={`/prompts/${promptId}/forks`}
            className="py-2 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 hover:text-gray-700 hover:border-gray-300"
          >
            <GitFork className="h-4 w-4 inline mr-1" />
            Forks
          </Link>
        </nav>
      </div>

      {/* Active Version Content */}
      {activeVersion && (
        <div className="card">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Active Version: {activeVersion.version_number}
              </h3>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  {activeVersion.author_name || 'Unknown'}
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {formatDistanceToNow(new Date(activeVersion.created_at), { addSuffix: true })}
                </div>
              </div>
            </div>
            {activeVersion.change_message && (
              <p className="text-gray-600 mt-2">{activeVersion.change_message}</p>
            )}
          </div>
          <div className="p-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="whitespace-pre-wrap text-sm font-mono">{activeVersion.content}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Version History */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Version History ({branch.versions?.length || 0})
          </h3>
        </div>
        <div className="divide-y divide-gray-200">
          {branch.versions?.map((version) => (
            <div key={version.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-gray-900">
                      Version {version.version_number}
                    </h4>
                    {version.is_active && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </div>
                  
                  {version.change_message && (
                    <p className="text-gray-600 mb-3">{version.change_message}</p>
                  )}
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      {version.author_name || 'Unknown'}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  {!version.is_active && (
                    <button
                      onClick={() => handleActivateVersion(version.id)}
                      className="btn-secondary text-sm"
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Activate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Version Modal */}
      {showCreateVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create New Version for Branch: {branch.name}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="versionContent" className="block text-sm font-medium text-gray-700 mb-1">
                  Prompt Content
                </label>
                <textarea
                  id="versionContent"
                  value={newVersion.content}
                  onChange={(e) => setNewVersion({ ...newVersion, content: e.target.value })}
                  className="input font-mono"
                  rows={12}
                  placeholder="Enter your prompt content here..."
                  required
                />
              </div>
              
              <div>
                <label htmlFor="changeMessage" className="block text-sm font-medium text-gray-700 mb-1">
                  Change Message
                </label>
                <input
                  type="text"
                  id="changeMessage"
                  value={newVersion.changeMessage}
                  onChange={(e) => setNewVersion({ ...newVersion, changeMessage: e.target.value })}
                  className="input"
                  placeholder="Describe what changed in this version..."
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleCreateVersion}
                className="btn-primary"
                disabled={!newVersion.content.trim()}
              >
                Create Version
              </button>
              <button
                onClick={() => {
                  setShowCreateVersion(false);
                  setNewVersion({ content: '', changeMessage: '' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchDetail; 